import PartnerIndustryRules from '../partner-industry-rules';

// ─── Types ───────────────────────────────────────────────────────────────────

export type NexusSearchHitProperty = {
    name: string;
    value?: unknown;
};

export type NexusSearchHit = {
    searchScore?: number;
    images?: (string | null)[];
    partNo: string;
    title?: string;
    id?: string | number;
    rootCategoryName?: string;
    leafCategoryName?: string;
    codes?: string[];
    tags?: unknown[];
    additionalProperties?: NexusSearchHitProperty[];
    variety?: string | null;
    deliveryMode?: unknown;
    [key: string]: unknown;
};

export type VariancePropertyDef = {
    id: string;
    name: string;
    title?: string;
    order: number;
};

export type AggregatedVariantMember = NexusSearchHit & {
    variantDetails: Record<string, {raw: unknown; formatted: string}>;
};

export type AggregatedVariantCard = NexusSearchHit & {
    /** True when this card represents a multi-SKU variety family. */
    isAggregated: boolean;
    varietyKey: string;
    members: AggregatedVariantMember[];
    selectedPartNo: string;
    /** Count of distinct SKUs in the family. */
    variantCount: number;
    /**
     * Stable family ranking score (max member searchScore).
     * Independent of selectedPartNo so main/relevant bucketing does not jump on option picks.
     */
    relevanceScore: number;
};

export type AggregationFetchConfig = {
    apiPageSize: number;
    uiPageSize: number;
    maxExtraFillPages: number;
    lookAheadPages: number;
    maxApiCallsPerLoad: number;
};

export type AggregationDictionary = Map<string, AggregatedVariantMember[]>;

export type AggregationSessionState = {
    /** Next API page to request (1-based). */
    nextApiPage: number;
    /** Variety keys already emitted to the UI (order preserved). */
    emittedVarietyKeys: string[];
    /** All known groups accumulated across fills / look-ahead. */
    dictionary: AggregationDictionary;
    /** Whether more server pages may exist. */
    hasMoreServerPages: boolean;
    /** Last known server totalCount (SKU-level; not shown while aggregating). */
    serverTotalCount: number;
};

export type SearchFilter = {
    filterId?: string | number;
    value?: unknown;
};

export type SchemaEntryLike = {
    id?: string | number;
    name?: string;
    title?: string;
};

export type HierarchicalOptionGroup = {
    id: string;
    name: string;
    title: string;
    order: number;
    options: Array<{id: unknown; title: string}>;
};

export type FetchAggregatedPageParams = {
    fetchPage: (apiPage: number, pageSize: number) => Promise<{
        hits: NexusSearchHit[];
        totalCount: number;
        meta?: unknown;
    }>;
    session: AggregationSessionState;
    varianceProperties?: VariancePropertyDef[];
    config?: Partial<AggregationFetchConfig>;
    previousCards?: AggregatedVariantCard[];
    reset?: boolean;
};

export type FetchAggregatedPageResult = {
    session: AggregationSessionState;
    /** Full list of cards for all emitted variety keys (enriched). */
    cards: AggregatedVariantCard[];
    /** Cards newly added this load (for append on infinite scroll). */
    emittedThisLoad: AggregatedVariantCard[];
    apiCalls: number;
    firstPageMeta?: unknown;
};

export type AggregatedSuggestionRow = {
    key: string;
    value: string;
    display: string;
    name: string;
    typeTitle: string;
    filterId: null;
    variantCount: number;
    isAggregatedPart: true;
};

const NULL_VALUE_PLACEHOLDER = '—';

export const DEFAULT_AGGREGATION_FETCH_CONFIG: AggregationFetchConfig = {
    apiPageSize: 60,
    uiPageSize: 30,
    maxExtraFillPages: 3,
    lookAheadPages: 1,
    maxApiCallsPerLoad: 4,
};

/**
 * Client-side Nexus Aggregated Variants search (group by variety, option pickers, look-ahead fill).
 */
class AggregatedVariantsNexusSearch {
    /**
     * Creates an empty aggregation session.
     */
    static createEmptySession(): AggregationSessionState {
        return {
            nextApiPage: 1,
            emittedVarietyKeys: [],
            dictionary: new Map(),
            hasMoreServerPages: true,
            serverTotalCount: 0,
        };
    }

    /**
     * Partner package OR industry allowlist. Facet gate is separate (results page).
     */
    static canUse(params: {
        accessEnabled?: boolean;
        industry?: {id?: string | number} | null;
    }): boolean {
        return Boolean(params.accessEnabled)
            || PartnerIndustryRules.isVarianceAggregationIndustry(params.industry);
    }

    /**
     * Aggregation check for suggestion / command surfaces (partner OR industry).
     */
    static shouldAggregateSuggestions(
        industry: {id?: string | number} | null | undefined,
        accessEnabled: boolean,
    ): boolean {
        return AggregatedVariantsNexusSearch.canUse({accessEnabled, industry});
    }

    /**
     * Returns true when any applied filter maps to a variance-hierarchy property.
     */
    static shouldDisableForFilters(
        filters: SearchFilter[] = [],
        schemaEntries: SchemaEntryLike[] = [],
        varianceProperties: VariancePropertyDef[] = [],
    ): boolean {
        if (!filters.length || !varianceProperties.length) {
            return false;
        }

        const varianceNames = new Set(
            varianceProperties.map((p) => String(p.name ?? '').trim().toLowerCase()).filter(Boolean),
        );
        const varianceIds = new Set(
            varianceProperties.map((p) => String(p.id ?? '').trim().toLowerCase()).filter(Boolean),
        );

        for (const filter of filters) {
            const filterId = filter?.filterId;
            if (filterId == null || filterId === '') {
                continue;
            }
            const schemaEntry = schemaEntries.find(
                (e) => String(e.id) === String(filterId),
            );
            if (!schemaEntry) {
                continue;
            }
            const name = String(schemaEntry.name ?? '').trim().toLowerCase();
            const id = String(schemaEntry.id ?? '').trim().toLowerCase();
            if (varianceNames.has(name) || varianceIds.has(id)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Builds variance property defs from industry property extra config.
     */
    static resolveVariancePropertiesFromExtraConfig(extraConfig?: {
        variants?: Array<string | number>;
        industryProperties?: Array<{id?: string | number; name?: string; title?: string}>;
    } | null): VariancePropertyDef[] {
        const variantIds = extraConfig?.variants ?? [];
        const industryProperties = extraConfig?.industryProperties ?? [];
        return variantIds
            .map((id, order) => {
                const prop = industryProperties.find((p) => String(p.id) === String(id));
                if (!prop?.name) {
                    return null;
                }
                return {
                    id: String(prop.id),
                    name: prop.name,
                    title: prop.title ?? prop.name,
                    order,
                } satisfies VariancePropertyDef;
            })
            .filter(Boolean) as VariancePropertyDef[];
    }

    /**
     * Reads a property value from a search hit by variance property name.
     */
    static getHitPropertyValue(hit: NexusSearchHit, propertyName: string): unknown {
        const prop = (hit.additionalProperties ?? []).find(
            (p) => String(p.name).toLowerCase() === String(propertyName).toLowerCase(),
        );
        return prop?.value;
    }

    /**
     * Variety group key for a hit. Hits without variety are singleton groups by partNo.
     */
    static resolveVarietyKey(hit: NexusSearchHit): string {
        const variety = hit.variety != null ? String(hit.variety).trim() : '';
        if (variety) {
            return `variety:${variety}`;
        }
        return `part:${hit.partNo}`;
    }

    /**
     * Builds variantDetails map for a hit against the variance hierarchy.
     */
    static buildMemberVariantDetails(
        hit: NexusSearchHit,
        varianceProperties: VariancePropertyDef[],
    ): Record<string, {raw: unknown; formatted: string}> {
        const details: Record<string, {raw: unknown; formatted: string}> = {};
        for (const prop of varianceProperties) {
            const raw = AggregatedVariantsNexusSearch.getHitPropertyValue(hit, prop.name);
            const formatted =
                raw == null || raw === ''
                    ? NULL_VALUE_PLACEHOLDER
                    : String(raw);
            details[prop.id] = {raw: raw ?? null, formatted};
        }
        return details;
    }

    /**
     * Folds hits into the aggregation dictionary (mutates dictionary).
     */
    static foldHitsIntoDictionary(
        dictionary: AggregationDictionary,
        hits: NexusSearchHit[],
        varianceProperties: VariancePropertyDef[] = [],
    ): void {
        for (const hit of hits) {
            if (!hit?.partNo) {
                continue;
            }
            const key = AggregatedVariantsNexusSearch.resolveVarietyKey(hit);
            const member: AggregatedVariantMember = {
                ...hit,
                variantDetails: AggregatedVariantsNexusSearch.buildMemberVariantDetails(
                    hit,
                    varianceProperties,
                ),
            };
            const existing = dictionary.get(key) ?? [];
            const already = existing.some((m) => m.partNo === hit.partNo);
            if (already) {
                dictionary.set(
                    key,
                    existing.map((m) => {
                        if (m.partNo !== hit.partNo) {
                            return m;
                        }
                        const preferNew = (hit.searchScore ?? 0) >= (m.searchScore ?? 0);
                        return preferNew ? member : m;
                    }),
                );
                continue;
            }
            existing.push(member);
            dictionary.set(key, existing);
        }
    }

    /**
     * Picks the representative member (highest search score).
     */
    static pickRepresentative(members: AggregatedVariantMember[]): AggregatedVariantMember {
        return [...members].sort((a, b) => (b.searchScore ?? 0) - (a.searchScore ?? 0))[0];
    }

    /**
     * Stable family relevance for ranking/bucketing (max member searchScore).
     */
    static familyRelevanceScore(members: AggregatedVariantMember[]): number {
        if (!members?.length) {
            return 0;
        }
        return Math.max(...members.map((m) => m.searchScore ?? 0));
    }

    /**
     * Builds an AggregatedVariantCard from members.
     */
    static buildCard(
        varietyKey: string,
        members: AggregatedVariantMember[],
        selectedPartNo?: string,
    ): AggregatedVariantCard {
        const representative = AggregatedVariantsNexusSearch.pickRepresentative(members);
        const selected =
            members.find((m) => m.partNo === selectedPartNo)
            ?? representative;

        return {
            ...selected,
            isAggregated: members.length > 1,
            varietyKey,
            members,
            selectedPartNo: selected.partNo,
            variantCount: members.length,
            relevanceScore: AggregatedVariantsNexusSearch.familyRelevanceScore(members),
        };
    }

    /**
     * Emits the next batch of aggregated cards for the UI window.
     * Mutates emittedVarietyKeys by appending newly emitted keys.
     */
    static emitNextCards(
        dictionary: AggregationDictionary,
        emittedVarietyKeys: string[],
        uiPageSize: number,
    ): AggregatedVariantCard[] {
        const emittedSet = new Set(emittedVarietyKeys);
        const cards: AggregatedVariantCard[] = [];

        for (const [key, members] of dictionary.entries()) {
            if (cards.length >= uiPageSize) {
                break;
            }
            if (emittedSet.has(key)) {
                continue;
            }
            cards.push(AggregatedVariantsNexusSearch.buildCard(key, members));
            emittedVarietyKeys.push(key);
            emittedSet.add(key);
        }

        return cards;
    }

    /**
     * Re-builds already-emitted cards from the dictionary (after look-ahead enrichment).
     */
    static refreshEmittedCards(
        dictionary: AggregationDictionary,
        emittedVarietyKeys: string[],
        previousCards: AggregatedVariantCard[] = [],
    ): AggregatedVariantCard[] {
        const selectedByKey = new Map(
            previousCards.map((c) => [c.varietyKey, c.selectedPartNo]),
        );
        return emittedVarietyKeys
            .map((key) => {
                const members = dictionary.get(key);
                if (!members?.length) {
                    return null;
                }
                return AggregatedVariantsNexusSearch.buildCard(key, members, selectedByKey.get(key));
            })
            .filter(Boolean) as AggregatedVariantCard[];
    }

    /**
     * Hierarchical option groups constrained by prior selections (PDP-style).
     * Omits dimensions with only one distinct value.
     */
    static buildHierarchicalOptions(
        members: AggregatedVariantMember[],
        varianceProperties: VariancePropertyDef[],
        selectedDetails?: Record<string, {raw: unknown; formatted: string}> | null,
    ): HierarchicalOptionGroup[] {
        if (!members.length || !varianceProperties.length) {
            return [];
        }

        const groupsSorted = [...varianceProperties].sort((a, b) => a.order - b.order);

        return groupsSorted.map((group, idx) => {
            const priorGroups = groupsSorted.slice(0, idx);
            const filtered = members.filter((v) => {
                const details = v.variantDetails ?? {};
                return priorGroups.every((pg) => {
                    const selected = selectedDetails?.[pg.id];
                    if (!selected) {
                        return true;
                    }
                    return details?.[pg.id]?.raw === selected?.raw;
                });
            });

            const seen = new Set<string>();
            const options: Array<{id: unknown; title: string}> = [];
            for (const v of filtered) {
                const value = v.variantDetails?.[group.id];
                const rawValue = value?.raw ?? null;
                const seenKey = JSON.stringify(rawValue);
                if (seen.has(seenKey)) {
                    continue;
                }
                seen.add(seenKey);
                options.push({
                    id: rawValue,
                    title: value?.formatted ?? NULL_VALUE_PLACEHOLDER,
                });
            }

            options.sort((a, b) => {
                if (a.id === null && b.id === null) return 0;
                if (a.id === null) return 1;
                if (b.id === null) return -1;
                return String(a.id).localeCompare(String(b.id), undefined, {numeric: true});
            });

            return {
                id: group.id,
                name: group.name,
                title: group.title ?? group.name,
                order: group.order,
                options,
            };
        }).filter((group) => (group.options?.length ?? 0) > 1);
    }

    /**
     * Finds the member matching the given selection map (propertyId → raw value).
     */
    static findMemberForSelection(
        members: AggregatedVariantMember[],
        selection: Record<string, unknown>,
    ): AggregatedVariantMember | undefined {
        return members.find((m) =>
            Object.entries(selection).every(([propId, raw]) => m.variantDetails?.[propId]?.raw === raw),
        );
    }

    /**
     * Applies a single dimension change and returns the updated card.
     */
    static selectOption(
        card: AggregatedVariantCard,
        propertyId: string,
        rawValue: unknown,
        varianceProperties: VariancePropertyDef[],
    ): AggregatedVariantCard {
        const current = card.members.find((m) => m.partNo === card.selectedPartNo)
            ?? AggregatedVariantsNexusSearch.pickRepresentative(card.members);
        const groupsSorted = [...varianceProperties].sort((a, b) => a.order - b.order);
        const changedIndex = groupsSorted.findIndex((g) => g.id === propertyId);

        const selection: Record<string, unknown> = {};
        for (let i = 0; i < groupsSorted.length; i++) {
            const g = groupsSorted[i];
            if (i < changedIndex) {
                selection[g.id] = current.variantDetails?.[g.id]?.raw;
            } else if (i === changedIndex) {
                selection[g.id] = rawValue;
            }
        }

        let match = AggregatedVariantsNexusSearch.findMemberForSelection(card.members, selection);
        if (!match) {
            match = card.members.find((m) =>
                groupsSorted.slice(0, changedIndex + 1).every((g) => {
                    if (g.id === propertyId) {
                        return m.variantDetails?.[g.id]?.raw === rawValue;
                    }
                    return m.variantDetails?.[g.id]?.raw === current.variantDetails?.[g.id]?.raw;
                }),
            );
        }
        if (!match) {
            match = card.members.find((m) => m.variantDetails?.[propertyId]?.raw === rawValue)
                ?? current;
        }

        return AggregatedVariantsNexusSearch.buildCard(card.varietyKey, card.members, match.partNo);
    }

    /**
     * Fills until uiPageSize new distinct groups (or caps), then look-ahead merges into existing groups.
     */
    static async fetchPage(params: FetchAggregatedPageParams): Promise<FetchAggregatedPageResult> {
        const config: AggregationFetchConfig = {
            ...DEFAULT_AGGREGATION_FETCH_CONFIG,
            ...params.config,
        };
        const varianceProperties = params.varianceProperties ?? [];

        const session: AggregationSessionState = params.reset
            ? AggregatedVariantsNexusSearch.createEmptySession()
            : {
                ...params.session,
                dictionary: new Map(params.session.dictionary),
                emittedVarietyKeys: [...params.session.emittedVarietyKeys],
            };

        let apiCalls = 0;
        let firstPageMeta: unknown;

        const countUnemitted = () => {
            const emitted = new Set(session.emittedVarietyKeys);
            let n = 0;
            for (const key of session.dictionary.keys()) {
                if (!emitted.has(key)) {
                    n += 1;
                }
            }
            return n;
        };

        const fetchNextApiPage = async () => {
            const page = session.nextApiPage;
            const result = await params.fetchPage(page, config.apiPageSize);
            apiCalls += 1;
            session.nextApiPage = page + 1;
            session.serverTotalCount = result.totalCount ?? session.serverTotalCount;
            if (apiCalls === 1) {
                firstPageMeta = result.meta;
            }

            const hits = result.hits ?? [];
            AggregatedVariantsNexusSearch.foldHitsIntoDictionary(
                session.dictionary,
                hits,
                varianceProperties,
            );

            const reachedEnd =
                hits.length < config.apiPageSize
                || (result.totalCount > 0 && (page * config.apiPageSize) >= result.totalCount);
            if (reachedEnd) {
                session.hasMoreServerPages = false;
            }
        };

        let fillPages = 0;
        while (
            countUnemitted() < config.uiPageSize
            && session.hasMoreServerPages
            && apiCalls < config.maxApiCallsPerLoad
            && fillPages < config.maxExtraFillPages + 1
        ) {
            await fetchNextApiPage();
            fillPages += 1;
        }

        const newlyEmittedKeys: string[] = [];
        const keysSnapshot = [...session.emittedVarietyKeys];
        const emittedThisLoad = AggregatedVariantsNexusSearch.emitNextCards(
            session.dictionary,
            session.emittedVarietyKeys,
            config.uiPageSize,
        );
        for (const key of session.emittedVarietyKeys) {
            if (!keysSnapshot.includes(key)) {
                newlyEmittedKeys.push(key);
            }
        }

        let lookAhead = 0;
        while (
            lookAhead < config.lookAheadPages
            && session.hasMoreServerPages
            && apiCalls < config.maxApiCallsPerLoad
        ) {
            await fetchNextApiPage();
            lookAhead += 1;
        }

        const previousByKey = new Map(
            (params.previousCards ?? []).map((c) => [c.varietyKey, c]),
        );
        for (const card of emittedThisLoad) {
            previousByKey.set(card.varietyKey, card);
        }

        const cards = AggregatedVariantsNexusSearch.refreshEmittedCards(
            session.dictionary,
            session.emittedVarietyKeys,
            [...previousByKey.values()],
        );

        const newlyEmittedSet = new Set(newlyEmittedKeys);
        const emittedThisLoadRefreshed = cards.filter((c) => newlyEmittedSet.has(c.varietyKey));

        return {
            session,
            cards,
            emittedThisLoad: params.reset ? cards : emittedThisLoadRefreshed,
            apiCalls,
            firstPageMeta,
        };
    }

    /**
     * Runs a lightweight Nexus product search and returns aggregated variety cards.
     */
    static async fetchSuggestionGroups(params: {
        fetchHits: (pageSize: number) => Promise<NexusSearchHit[]>;
        limit?: number;
    }): Promise<AggregatedVariantCard[]> {
        const limit = params.limit ?? 5;
        const result = await AggregatedVariantsNexusSearch.fetchPage({
            reset: true,
            session: AggregatedVariantsNexusSearch.createEmptySession(),
            config: {
                apiPageSize: Math.max(20, limit * 4),
                uiPageSize: limit,
                maxExtraFillPages: 1,
                lookAheadPages: 0,
                maxApiCallsPerLoad: 2,
            },
            fetchPage: async (_apiPage, pageSize) => {
                const hits = await params.fetchHits(pageSize);
                return {hits, totalCount: hits.length};
            },
        });
        return result.emittedThisLoad.slice(0, limit);
    }

    /**
     * Maps aggregated cards to autocomplete / command suggestion rows (part families).
     */
    static toSuggestionRows(
        cards: AggregatedVariantCard[],
        partNoSchemaName: string,
        partNoTypeTitle: string,
    ): AggregatedSuggestionRow[] {
        return cards.map((card) => ({
            key: card.varietyKey,
            value: card.selectedPartNo ?? card.partNo,
            display: card.title
                ? `${card.selectedPartNo ?? card.partNo} — ${card.title}`
                : (card.selectedPartNo ?? card.partNo),
            name: partNoSchemaName,
            typeTitle: partNoTypeTitle,
            filterId: null,
            variantCount: card.variantCount,
            isAggregatedPart: true as const,
        }));
    }
}

export default AggregatedVariantsNexusSearch;
